import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { ToastMessageLevel, ToastProps } from 'views/components/atoms/toast';
import { create } from 'zustand';

type GlobalToastStore = ToastProps & {
  shownLocation: string;
  showInfo: (message: string, location: string) => void;
  showError: (message: string, location: string) => void;
};

const useGlobalToastStore = create<GlobalToastStore>((set) => ({
  isActive: false,
  label: '',
  messageLevel: ToastMessageLevel.Error,
  shownLocation: '',
  setIsActive: () => set(() => ({ isActive: false })),
  showInfo: (message: string, location: string) =>
    set(() => ({ label: message, isActive: true, messageLevel: ToastMessageLevel.Info, shownLocation: location })),
  showError: (message: string, location: string) =>
    set(() => ({ label: message, isActive: true, messageLevel: ToastMessageLevel.Error, shownLocation: location })),
}));

export const useGlobalToast = (): ToastProps => {
  const isActive = useGlobalToastStore((state) => state.isActive);
  const label = useGlobalToastStore((state) => state.label);
  const messageLevel = useGlobalToastStore((state) => state.messageLevel);
  const setIsActive = useGlobalToastStore((state) => state.setIsActive);

  return {
    isActive,
    label,
    messageLevel,
    setIsActive,
  };
};

export const useGlobalToastHandler = () => {
  const location = useLocation();
  const shownLocation = useGlobalToastStore((state) => state.shownLocation);
  const setIsActive = useGlobalToastStore((state) => state.setIsActive);
  const showInfoInner = useGlobalToastStore((state) => state.showInfo);
  const showErrorInner = useGlobalToastStore((state) => state.showError);
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout>>();

  const showInfo = useCallback(
    (message: string) => {
      // 連続で呼ばれた場合に意図せずに非表示になるのでclearTimeoutする
      clearTimeout(timeoutIdRef.current);
      showInfoInner(message, location.pathname);

      timeoutIdRef.current = setTimeout(() => {
        setIsActive(false);
      }, 4000);
    },
    [location],
  );

  const showError = useCallback(
    (message: string | null | undefined, durationMs?: number) => {
      clearTimeout(timeoutIdRef.current);
      showErrorInner(message || 'エラーが発生しました。', location.pathname);

      if (durationMs) {
        timeoutIdRef.current = setTimeout(() => {
          setIsActive(false);
        }, durationMs);
      }
    },
    [location],
  );

  // 画面遷移した場合は非表示にする
  useEffect(() => {
    // 別インスタンスで呼ばれているかもしれないので現在のlocationとStoreのlocationを比較する
    if (location.pathname !== shownLocation) {
      clearTimeout(timeoutIdRef.current);
      setIsActive(false);
    }
  }, [location, shownLocation]);

  return {
    showInfo,
    showError,
  };
};
